import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

const PropertyAlertsLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Real-Time Notifications',
      description: 'Get instant alerts for new listings matching your criteria',
      icon: '🔔'
    },
    {
      title: 'Custom Filters',
      description: 'Set price range, location, property type, and more',
      icon: '🎯'
    },
    {
      title: 'Market Intelligence',
      description: 'Receive market trend updates and price insights',
      icon: '📊'
    },
    {
      title: 'Multi-Criteria Search',
      description: 'Filter by bedrooms, amenities, availability, and location',
      icon: '🏠'
    },
    {
      title: 'Never Miss an Opportunity',
      description: 'Be the first to know about properties in your target areas',
      icon: '⚡'
    },
    {
      title: 'Email & App Alerts',
      description: 'Choose how and when you want to be notified',
      icon: '📧'
    }
  ];

  const benefits = [
    'Get alerts before properties are listed elsewhere',
    'Find properties matching exactly what you want',
    'Make faster decisions with instant notifications',
    'Track multiple locations and price ranges',
    'Never overpay - monitor market trends'
  ];

  const faqItems = [
    {
      question: 'How fast are property alerts sent?',
      answer: 'Alerts are triggered in near real time so you can respond before listings get crowded.',
    },
    {
      question: 'Can I create multiple alerts?',
      answer: 'Yes. Track several cities, counties, or budgets at once with separate alerts.',
    },
    {
      question: 'Do alerts cover rentals and sales?',
      answer: 'You can configure alerts for rentals, sales, or both depending on your search goals.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Property Alerts Ireland | Get Instant Listing Notifications | RentSmart.ie</title>
        <meta
          name="description"
          content="Stay ahead with RentSmart's Property Alerts. Get instant notifications for new listings in Dublin, Cork, Galway, and across Ireland. Free alerts for renters and buyers."
        />
        <meta
          name="keywords"
          content="property alerts, real estate alerts Ireland, listing notifications, Dublin rentals, Cork properties, rental alerts, property search Ireland, automatic alerts"
        />
        <link rel="canonical" href="https://rentsmart.ie/property-tools/property-alerts-landing" />
        <meta property="og:title" content="Property Alerts - RentSmart.ie" />
        <meta
          property="og:description"
          content="Get instant alerts for new property listings matching your criteria across Ireland."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://rentsmart.ie/property-tools/property-alerts-landing"
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Never Miss a
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                Perfect Property
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Get instant alerts for properties matching your criteria. Be the first to know about
              new listings in Ireland.
            </p>
            <button
              onClick={() => navigate('/property-tools/property-alerts')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Create Your First Alert
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Smart Alert System
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Set Up Property Alerts?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircleIcon className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-orange-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Coverage Across Ireland
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                'Dublin',
                'Cork',
                'Galway',
                'Limerick',
                'Waterford',
                'Kilkenny',
                'Donegal',
                'Sligo',
                'Mayo',
              ].map((city) => (
                <div
                  key={city}
                  className="bg-white rounded-lg p-4 text-center border border-orange-200"
                >
                  <p className="font-semibold text-gray-900">{city}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-600 mt-6">...and all other counties in Ireland</p>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                {
                  step: 1,
                  title: 'Set Criteria',
                  description: 'Define your perfect property',
                },
                {
                  step: 2,
                  title: 'Create Alert',
                  description: 'Set up with your preferences',
                },
                {
                  step: 3,
                  title: 'Get Notified',
                  description: 'Receive instant alerts',
                },
                {
                  step: 4,
                  title: 'Take Action',
                  description: 'Contact landlords immediately',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customization */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Fully Customizable
            </h2>
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    📍 Location Preferences
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Specific counties or neighborhoods</li>
                    <li>✓ Distance radius from a location</li>
                    <li>✓ Multiple areas simultaneously</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    💰 Price & Property Details
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Min and max price range</li>
                    <li>✓ Number of bedrooms/bathrooms</li>
                    <li>✓ Property type (apartment, house, etc.)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    🏠 Amenities & Features
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Parking availability</li>
                    <li>✓ Pet-friendly properties</li>
                    <li>✓ Virtual tours available</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    🔔 Notification Preferences
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Email notifications</li>
                    <li>✓ App push notifications</li>
                    <li>✓ Frequency settings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-600 to-red-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Never Miss Out on Your Perfect Property
            </h2>
            <p className="text-lg mb-8 text-orange-100">
              Set up alerts now and be notified instantly when new properties match your criteria
            </p>
            <button
              onClick={() => navigate('/property-tools/property-alerts')}
              className="inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Set Up Alerts Now
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
              Property Alerts FAQs
            </h2>
            <div className="space-y-6">
              {faqItems.map((item) => (
                <div key={item.question} className="bg-slate-50 border border-slate-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-orange-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{item.question}</h3>
                      <p className="text-gray-700 mt-2">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PropertyAlertsLandingPage;
